import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { CheckboxField } from '@/components/form/fields/checkbox/Checkbox';
import { TextAndIconField } from '@/components/form/fields/text/TextAndIconInput';
import { TextField } from '@/components/form/fields/text/TextInput';
import { SelectInput } from '@/components/form/fields/select/SelectInput';
import { CategoryIcons } from '@/components/icons/CategoryIcons';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { Form } from '../../../components/form/Form';
import { Modal } from '../../../components/modal/Modal';
import { ModalProps } from '../../../components/modal/useModal';
import { Card } from '../../../components/sections/Card';
import { useCategories, useUpdateCategory, useGetRateCards } from '../../../services/api/hooks/catalog';
import TenantRestrictionTree from '@/components/org/TenantRestrictionTree';
import { useProcessOrgTreeForTenantRestrictions } from '@/components/org/useProcessOrgTreeForTenantRestrictions';

const DirectionalLogisticsFields = z.object({
  averageWeightKg: z.coerce.number().optional(),
  dimensionalWeightKg: z.coerce.number().optional(),
  packingEfficiencyPercent: z.coerce.number().optional(),
  overrideRateCardId: z.string().optional(),
  itemProcessingRateCardId: z.string().optional()
});

const EditCategoryFormFields = z.object({
  name: z.object({
    text: z.string(),
    iconId: z.string()
  }),
  isMainDeviceCategory: z.boolean(),
  isSerialized: z.boolean().optional(),
  hideFromGenerics: z.boolean().optional(),
  deploy: DirectionalLogisticsFields.optional(),
  return: DirectionalLogisticsFields.optional()
}).superRefine((data, ctx) => {
  if (!data.hideFromGenerics) {
    if (data.deploy && (data.deploy.averageWeightKg || data.deploy.dimensionalWeightKg)) {
      if (data.deploy.packingEfficiencyPercent === undefined || data.deploy.packingEfficiencyPercent === null || data.deploy.packingEfficiencyPercent < 1 || data.deploy.packingEfficiencyPercent > 100) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Packing Efficiency is required (1-100) for physical categories',
          path: ['deploy', 'packingEfficiencyPercent']
        });
      }
    }
    if (data.return && (data.return.averageWeightKg || data.return.dimensionalWeightKg)) {
      if (data.return.packingEfficiencyPercent === undefined || data.return.packingEfficiencyPercent === null || data.return.packingEfficiencyPercent < 1 || data.return.packingEfficiencyPercent > 100) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Packing Efficiency is required (1-100) for physical categories',
          path: ['return', 'packingEfficiencyPercent']
        });
      }
    }
  }
});

type EditCategoryFormFields = z.infer<typeof EditCategoryFormFields>;

export const EditCategoryModal: FC<ModalProps<{ categoryId: string }>> = props => {
  const EditCategory = () => {
    const [categories] = useCategories();
    const categoryId = props.props?.categoryId;
    if (!categoryId) throw new Error('Missing categoryId');
    const category = categories?.find(category => category.id === categoryId);
    
    const [rateCardsResp, { isLoading: isRateCardsLoading }] = useGetRateCards();
    const overrideRateCards = useMemo(() => {
      if (isRateCardsLoading) return [];
      return rateCardsResp?.items?.filter(r => r.propertyBag?.isExclusiveOverride === true || String(r.propertyBag?.isExclusiveOverride) === 'true').map(r => ({ id: r.id, name: r.name })) || [];
    }, [rateCardsResp, isRateCardsLoading]);

    const itemProcessingRateCards = useMemo(() => {
      if (isRateCardsLoading) return [];
      return rateCardsResp?.items?.filter(r => r.propertyBag?.isItemRateOverride === true || String(r.propertyBag?.isItemRateOverride) === 'true').map(r => ({ id: r.id, name: r.name })) || [];
    }, [rateCardsResp, isRateCardsLoading]);

    const deployFallback = useMemo(() => {
      const defaultCard = rateCardsResp?.items?.find(r => r.propertyBag?.itemRateForRequestType === 'DeployInventory');
      return { id: '', name: defaultCard ? `Default (${defaultCard.name})` : 'Default' };
    }, [rateCardsResp]);

    const returnFallback = useMemo(() => {
      const defaultCard = rateCardsResp?.items?.find(r => r.propertyBag?.itemRateForRequestType === 'OffboardUser');
      return { id: '', name: defaultCard ? `Default (${defaultCard.name})` : 'Default' };
    }, [rateCardsResp]);

    const methods = useForm({
      resolver: zodResolver(EditCategoryFormFields),
      defaultValues: {
        name: { text: category?.name, iconId: category?.description },
        isMainDeviceCategory: category?.isMainDevice,
        isSerialized: category?.isSerialized,
        hideFromGenerics: category?.hideFromGenerics,
        deploy: category?.logisticsConfig?.deploy,
        return: category?.logisticsConfig?.return
      }
    });

    const orgTree = useProcessOrgTreeForTenantRestrictions(category?.hiddenFromTenantIds);
    const updateCategory = useUpdateCategory();

    const onSubmit = async (data: EditCategoryFormFields) => {
      const hiddenFromTenantIds = orgTree.reduce((ids: string[], msp) => {
        if (msp.checked) {
          ids.push(msp.mspId);
        }
        msp.customers.forEach(customer => {
          if (customer.checked) {
            ids.push(customer.customerId);
          }
        });
        return ids;
      }, []);

      await updateCategory.mutateAsync({
        id: categoryId,
        name: data.name.text,
        description: data.name.iconId,
        isMainDevice: data.isMainDeviceCategory,
        hiddenFromTenantIds,
        isSerialized: data.isSerialized || false,
        hideFromGenerics: data.hideFromGenerics || false,
        logisticsConfig: {
          deploy: data.deploy,
          return: data.return
        }
      });
      toast.success(`${data.name.text} updated`);
      props.close();
    };

    return (
      <Card className="w-full p-8 max-h-[90vh] overflow-y-auto">
        <Modal.Controls {...props}>
          <h1 className="text-2xl font-semibold">Edit Category</h1>
        </Modal.Controls>
        <Form className="flex w-full flex-col gap-3 py-3" methods={methods} onSubmit={onSubmit}>
          <TextAndIconField id="name" label="Name" icons={CategoryIcons} />
          
          <div className="flex flex-col gap-2 mt-2">
            <Controller
              name="isMainDeviceCategory"
              control={methods.control}
              render={({ field }) => (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={field.value || false} onChange={e => field.onChange(e.target.checked)} className="w-4 h-4 text-primary-700" />
                  <span className="text-sm font-medium text-gray-700">Main Device Category</span>
                </label>
              )}
            />
            <Controller
              name="hideFromGenerics"
              control={methods.control}
              render={({ field }) => (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={field.value || false} onChange={e => field.onChange(e.target.checked)} className="w-4 h-4 text-primary-700" />
                  <span className="text-sm font-medium text-gray-700">Hide from Generic Returns List</span>
                </label>
              )}
            />
            <Controller
              name="isSerialized"
              control={methods.control}
              render={({ field }) => (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={field.value || false} onChange={e => field.onChange(e.target.checked)} className="w-4 h-4 text-primary-700" />
                  <span className="text-sm font-medium text-gray-700">Serialised Return Required</span>
                </label>
              )}
            />
          </div>
          
          <div className="space-y-4 mt-4 border-t pt-4">
            <h2 className="text-lg font-medium">Deployment Logistics</h2>
            <div className="grid grid-cols-2 gap-4">
              <TextField id="deploy.averageWeightKg" label="Average Weight (kg)" onlyDigits={true} />
              <TextField id="deploy.dimensionalWeightKg" label="Dimensional Weight (kg)" onlyDigits={true} />
              <TextField id="deploy.packingEfficiencyPercent" label="Packing Efficiency (%)" onlyDigits={true} />
              <Controller
                name="deploy.overrideRateCardId"
                control={methods.control}
                render={({ field }) => (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Override Logistics With (Optional)</label>
                    <SelectInput
                      id="deploy.overrideRateCardId"
                      options={[{ id: '', name: 'None' }, ...overrideRateCards]}
                      placeholder="None"
                      value={overrideRateCards.find(r => r.id === field.value) || null}
                      onChange={(val: any) => field.onChange(val?.id || undefined)}
                    />
                  </div>
                )}
              />
              <Controller
                name="deploy.itemProcessingRateCardId"
                control={methods.control}
                render={({ field }) => (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Deployment Processing Rate Card (Optional)</label>
                    <SelectInput
                      id="deploy.itemProcessingRateCardId"
                      options={[deployFallback, ...itemProcessingRateCards]}
                      placeholder={deployFallback.name}
                      value={itemProcessingRateCards.find(r => r.id === field.value) || null}
                      onChange={(val: any) => field.onChange(val?.id || undefined)}
                    />
                  </div>
                )}
              />
            </div>
          </div>

          <div className="space-y-4 mt-4 border-t pt-4">
            <h2 className="text-lg font-medium">Return Logistics</h2>
            <div className="grid grid-cols-2 gap-4">
              <TextField id="return.averageWeightKg" label="Average Weight (kg)" onlyDigits={true} />
              <TextField id="return.dimensionalWeightKg" label="Dimensional Weight (kg)" onlyDigits={true} />
              <TextField id="return.packingEfficiencyPercent" label="Packing Efficiency (%)" onlyDigits={true} />
              <Controller
                name="return.overrideRateCardId"
                control={methods.control}
                render={({ field }) => (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Override Logistics With (Optional)</label>
                    <SelectInput
                      id="return.overrideRateCardId"
                      options={[{ id: '', name: 'None' }, ...overrideRateCards]}
                      placeholder="None"
                      value={overrideRateCards.find(r => r.id === field.value) || null}
                      onChange={(val: any) => field.onChange(val?.id || undefined)}
                    />
                  </div>
                )}
              />
              <Controller
                name="return.itemProcessingRateCardId"
                control={methods.control}
                render={({ field }) => (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Return Processing Rate Card (Optional)</label>
                    <SelectInput
                      id="return.itemProcessingRateCardId"
                      options={[returnFallback, ...itemProcessingRateCards]}
                      placeholder={returnFallback.name}
                      value={itemProcessingRateCards.find(r => r.id === field.value) || null}
                      onChange={(val: any) => field.onChange(val?.id || undefined)}
                    />
                  </div>
                )}
              />
            </div>
          </div>
          
          <div className="space-y-1 border-t pt-4">
            <h2 className="text-lg font-medium">Hide from Tenants</h2>
            <p className="text-sm text-gray-500">Select MSPs or Customers that should NOT see this category.</p>
            <TenantRestrictionTree data={orgTree} />
          </div>

          <Button className="w-fit place-self-end mt-4" variant={ButtonVariant.Primary} type="submit">
            Update
          </Button>
        </Form>
      </Card>
    );
  };

  return (
    <Modal className="md:max-w-2xl" {...props}>
      <EditCategory />
    </Modal>
  );
};
